import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Chapter } from "../components/Chapter";
import { Reveal, MaskedLine } from "../lib/motion";
import { usePageSeo } from "../lib/i18nContext";
import LocalizedLink from "../components/LocalizedLink";
import { StructuredLines } from "../components/Motifs";

const WRAP = "max-w-[1240px] mx-auto px-6 md:px-12 xl:px-16";
const H2 = "font-display text-[26px] md:text-[32px] font-semibold tracking-tight text-ink leading-tight";

const Quality = () => {
  usePageSeo("quality");
  const { t } = useTranslation();
  const docs = t("docs.items", { returnObjects: true });

  return (
    <main className="pt-nav">
      <section data-testid="quality-hero" className="bg-white border-b border-ice">
        <div className={`${WRAP} py-16 md:py-24`}>
          <p className="font-mono text-xs md:text-[13px] font-medium tracking-[0.18em] uppercase text-pine mb-8">{t("qualityPage.eyebrow")}</p>
          <h1 className="font-display font-semibold text-ink text-[36px] md:text-[44px] tracking-tight leading-[1.12] max-w-4xl" data-testid="quality-headline">
            <MaskedLine delay={0.1}>{t("qualityPage.line1")}</MaskedLine>
            <MaskedLine delay={0.2}>{t("qualityPage.line2")}</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-white">
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
          <div className="lg:col-span-8 max-w-3xl">
            <Chapter index="01" label={t("qualityPage.s1.chapter")} />
            <Reveal delay={0.05}>
              <h2 className={`${H2} mt-8`}>{t("qualityPage.s1.heading")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-slate leading-relaxed mt-8 text-base md:text-lg">{t("qualityPage.s1.p1")}</p>
              <p className="text-slate leading-relaxed mt-6">{t("qualityPage.s1.p2")}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-start justify-end" aria-hidden="true">
            <StructuredLines cols={4} rows={4} className="w-44" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-ice">
        <div className={WRAP}>
          <Chapter index="02" label={t("qualityPage.s2.chapter")} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8">
            <Reveal className="lg:col-span-4">
              <h2 className={H2}>{t("qualityPage.s2.heading")}</h2>
              <p className="font-mono text-xs tracking-[0.16em] uppercase text-deep mt-6">{t("qualityPage.s2.whereApplicable")}</p>
            </Reveal>
            <ol className="lg:col-span-8 border-t border-ink/10 bg-white">
              {docs.map((d, i) => (
                <li key={d} className="flex items-center gap-5 px-6 md:px-8 py-5 border-b border-s border-e border-ink/10" data-testid={`doc-item-${i}`}>
                  <span className="font-mono text-xs text-pine w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-ink font-medium">{d}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className={WRAP}>
          <div className="border border-ice rounded p-8 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <Reveal>
                <h2 className={H2}>{t("qualityPage.box.heading")}</h2>
                <p className="text-slate leading-relaxed mt-5 max-w-2xl">{t("qualityPage.box.text")}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <LocalizedLink to="/contact" data-testid="quality-cta" className="inline-flex items-center gap-2 bg-ink text-white font-semibold px-8 py-4 rounded text-sm transition-colors duration-300 hover:bg-deep">
                {t("qualityPage.box.cta")} <ArrowRight size={16} className="rtl:-scale-x-100" />
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Quality;
